import { useEffect, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import { AudienceComparisonParams } from "../../types/audience-compare";
import { audienceCompareApi } from "../../api";
import { mapResponseToCellData } from "../../utils/audience-compare";
import { unflatten } from "../../utils/array";

const queryKeys = {
  all: () => ["audience-compare"] as const,
  audienceSettingOptions: (accountId: number) =>
    [queryKeys.all(), "audience-setting-options", { accountId }] as const,
  comparison: (params: AudienceComparisonParams) => [
    ...queryKeys.all(),
    "comparison",
    params,
  ],
};

/**
 * A single query which returns both sources and baselines.
 * Combined because the UI expects all or nothing.
 */
export const useAudienceSettingOptions = (accountId: number) =>
  useQuery({
    queryKey: queryKeys.audienceSettingOptions(accountId),
    queryFn: () => audienceCompareApi.getAudienceSettingOptions(accountId),
  });

/**
 * Fetches data for the results tables.
 *
 * The data will be returned as a 2D array.
 */
export const useAudienceComparisons = (
  params: AudienceComparisonParams[][],
) => {
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);

  // generate ALL of the queries using a flattened array
  const queries = useQueries({
    queries: params.flat().map((x, i) => ({
      queryKey: queryKeys.comparison(x),
      queryFn: () => audienceCompareApi.getAudienceComparisonRow(x),
      staleTime: 1000 * 60 * 10,
      enabled: i <= currentQueueIndex,
    })),
  });

  // increase the currentQueueIndex as data loads (or errors)
  useEffect(() => {
    const query = queries[currentQueueIndex];
    if (!!query?.data || !!query?.error) {
      if (currentQueueIndex < queries.length) {
        setCurrentQueueIndex(currentQueueIndex + 1);
      }
    }
  }, [queries, currentQueueIndex, setCurrentQueueIndex]);

  return unflatten(params, queries.map(mapResponseToCellData));
};
